import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Estado da sincronização multi-dispositivo (ver docs/features/SYNC.md, ADR 0030).
 *
 * Separado do backup no Drive (useDriveBackupStore) de propósito: sync e backup são recursos
 * distintos que compartilham só a conexão Google + a senha E2E. `enabled` liga a captura por
 * triggers + o ciclo oportunístico; a conexão em si mora no useDriveBackupStore.
 */
interface SyncState {
    /** Sync ligado (recurso Pro — o gate é conectar, igual ao backup). */
    enabled: boolean;
    /** Último ciclo de sync bem-sucedido (ISO). */
    lastSyncAt: string | null;
    /** Última falha (observabilidade/CTA). null = último ciclo ok. */
    lastError: string | null;
    /**
     * Ciclos SEGUIDOS que falharam desde o último bom. É o que a Home lê pra acusar o sync parado
     * (ver `isSyncStalled`): uma falha isolada é rede oscilando e some no poll seguinte; várias
     * em fila são problema de verdade (aparelhos em versões diferentes, senha divergente, chave
     * perdida numa troca de aparelho). Contador em vez de relógio de propósito: a Home decide
     * só com o estado da store, sem `Date.now()` no render.
     */
    failureStreak: number;
    /**
     * Prova de que a base local ainda é o snapshot restaurado, e portanto ainda está CONTIDA no
     * espaço de sync — o que dispensa a confirmação destrutiva ao entrar nele.
     *
     * Os dois campos viajam JUNTOS de propósito. `syncedAt` (do envelope do backup, ver
     * BackupSnapshotMeta) é a afirmação; `fingerprint` (localDataFingerprint no instante do
     * restore) é a prova de que nada foi escrito desde então. Guardar só a afirmação era um bug:
     * ela é persistida e não expira sozinha, então bastava restaurar, usar o app com o sync
     * desligado e ligar depois pra o join apagar em silêncio tudo o que foi criado no meio.
     *
     * Zerado ao entrar no espaço (daí em diante quem prova a linhagem é o oplog local) e ao
     * desligar o sync.
     */
    restoredSnapshot: { syncedAt: string; fingerprint: string } | null;
    /**
     * Ligar falhou porque a senha não bate com a do espaço (`SyncPassphraseMismatchError`).
     * PERSISTIDO: o card mostra o quadro "A senha não bate" até a pessoa resolver — antes era um
     * toast que sumia em segundos e deixava o card mudo. Limpa ao ligar com sucesso, ao
     * redefinir com sucesso, no fechar do quadro e no `reset()`.
     */
    enableFailure: 'pass_mismatch' | null;

    setEnabled: (enabled: boolean) => void;
    markSynced: (at?: string) => void;
    setError: (error: string | null) => void;
    setRestoredSnapshot: (snapshot: { syncedAt: string; fingerprint: string } | null) => void;
    setEnableFailure: (failure: 'pass_mismatch' | null) => void;
    /** Volta ao estado de instalação nova (factory reset). */
    reset: () => void;
}

export const useSyncStore = create<SyncState>()(
    persist(
        (set) => ({
            enabled: false,
            lastSyncAt: null,
            lastError: null,
            failureStreak: 0,
            restoredSnapshot: null,
            enableFailure: null,
            // Ligar ou desligar zera a saúde: uma falha de antes de desligar não pode acender o
            // aviso da Home no instante em que a pessoa religa, antes do primeiro ciclo novo.
            // Ligar com sucesso também resolve a "senha não bate".
            setEnabled: (enabled) =>
                set((s) => ({ enabled, lastError: null, failureStreak: 0, enableFailure: enabled ? null : s.enableFailure })),
            markSynced: (at) => set({ lastSyncAt: at ?? new Date().toISOString(), lastError: null, failureStreak: 0 }),
            setError: (lastError) =>
                set((s) => ({ lastError, failureStreak: lastError ? s.failureStreak + 1 : 0 })),
            setRestoredSnapshot: (restoredSnapshot) => set({ restoredSnapshot }),
            setEnableFailure: (enableFailure) => set({ enableFailure }),
            reset: () =>
                set({ enabled: false, lastSyncAt: null, lastError: null, failureStreak: 0, restoredSnapshot: null, enableFailure: null }),
        }),
        {
            name: '@finapp_sync',
            storage: createJSONStorage(() => AsyncStorage),
        },
    ),
);

/** Falhas seguidas a partir das quais a Home acusa o sync parado (ADR 0146). */
export const SYNC_STALLED_AFTER_FAILURES = 2;

/**
 * O sync está ligado e parou de funcionar? Com o sync saudável a Home não mostra nada (ADR 0146):
 * só esta resposta `true` acende o aviso. Pro e conta conectada o componente confere à parte —
 * sem eles o ciclo nem tenta rodar, e o card de Backup já explica.
 */
export function isSyncStalled(state: Pick<SyncState, 'enabled' | 'failureStreak'>): boolean {
    return state.enabled && state.failureStreak >= SYNC_STALLED_AFTER_FAILURES;
}

export async function hydrateSyncStore(): Promise<void> {
    try {
        if (useSyncStore.persist.hasHydrated()) return;
        await useSyncStore.persist.rehydrate();
    } catch {
        // Mantém os defaults se o AsyncStorage falhar.
    }
}
